import React from "react";
import Select from "react-select";
import Button from "components/button/Button";
import CommonModal from "components/modals/Modal";
import InputField from "components/fields/InputField";
import ProductCard from "components/card/ProductCard";
import { debounce } from "lodash";
import { toast } from "react-hot-toast";
import { createPortal } from "react-dom";
import { FiSearch } from "react-icons/fi";
import { useForm } from "react-hook-form";
import { getBranchGoods, updateBranchGoodStock } from "api/BranchAPI";
import { PiDotsThreeBold } from "react-icons/pi";
import { IBranchGoods } from "../types/branch.interface";
import { useNavigate, useParams } from "react-router-dom";
import { BiChevronLeft, BiChevronRight } from "react-icons/bi";
import { GoHistory, GoPackageDependencies } from "react-icons/go";
import { getBranchsAssignLists, rollbackBranchStock } from "api/GoodsAPI";
import { NumberParam, StringParam, useQueryParams } from "use-query-params";
import {
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  ModalHeader,
  Spinner,
  useDisclosure,
} from "@chakra-ui/react";

const totalItemsOptions: {
  value: number;
}[] = [
  { value: 10 },
  { value: 20 },
  { value: 25 },
  { value: 50 },
  { value: 100 },
];

const BranchProduct = () => {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const { isOpen, onClose, onOpen } = useDisclosure();
  const {
    register,
    setValue,
    getValues,
    formState: { errors },
  } = useForm();

  const [isModalStockOpen, setIsModalStockOpen] =
    React.useState<boolean>(false);
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [isLoadingRollback, setIsLoadingRollback] =
    React.useState<boolean>(true);
  const [goods, setGoods] = React.useState<IBranchGoods[]>([]);
  const [rollbackList, setRollbackList] = React.useState<any[]>([]);
  const [selectedVersion, setSelectedVersion] = React.useState<any>(null);
  const [query, setQuery] = useQueryParams({
    page: NumberParam,
    ob: NumberParam,
    search: StringParam,
    limit: NumberParam,
  });
  const [limit, setLimit] = React.useState<number>(null);
  const [searchKey, setSearchKey] = React.useState<string>(null);
  const [nextPage, setNextPage] = React.useState<number>(null);
  const [prevPage, setPrevPage] = React.useState<number>(null);
  const [lastPage, setLastPage] = React.useState<number>(null);
  const [currentPage, setCurrentPage] = React.useState<number>(1);
  const [pageState, setPageState] = React.useState({
    startIdx: 0,
    endIdx: 0,
  });
  const [pageNumbers, setPageNumbers] = React.useState<number[]>([]);
  const [good, setGood] = React.useState<IBranchGoods>(null);

  const jumpForwardPage = () => {
    setPageState({
      startIdx: pageState.endIdx,
      endIdx: pageState.endIdx + 5,
    });
  };

  const jumpBackwardPage = () => {
    setPageState({
      startIdx: pageState.startIdx - 5,
      endIdx: pageState.endIdx - 5,
    });
  };

  const handleNextPage = () => {
    if (currentPage === lastPage) return;
    setCurrentPage(nextPage);
    getBranchProducts(nextPage, searchKey, limit);
  };

  const handlePrevPage = () => {
    if (currentPage === 1) return;
    setCurrentPage(prevPage);
    getBranchProducts(prevPage, searchKey, limit);
  };

  const getBranchProducts = (
    page?: number,
    keyword?: string,
    limit?: number
  ): void => {
    setIsLoading(true);
    const promise = getBranchGoods(slug, page, keyword, limit);

    promise
      .then((res) => {
        if (res.status === 200) {
          setIsLoading(false);
          setGoods(res.data.data.items);
          setNextPage(res.data.data.meta.next_page);
          setPrevPage(res.data.data.meta.prev_page);
          setLastPage(res.data.data.meta.last_page);
          setCurrentPage(res.data.data.meta.current_page);
          setPageNumbers(
            Array.from(Array(res.data.data.meta.last_page + 1).keys()).slice(1)
          );
          if (res.data.data.meta.current_page === pageState.endIdx + 1) {
            setPageState({
              startIdx: pageState.endIdx,
              endIdx: pageState.endIdx + 5,
            });
          } else if (res.data.data.meta.current_page === pageState.startIdx) {
            setPageState({
              startIdx: pageState.startIdx - 5,
              endIdx: pageState.endIdx - 5,
            });
          }
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || "Gagal memuat barang!");
      });
  };

  const getRollbackList = () => {
    setIsLoadingRollback(true);
    getBranchsAssignLists(slug)
      .then((res: any) => {
        setIsLoadingRollback(false);
        if (res.status === 200) setRollbackList(res.data?.data?.data);
      })
      .catch((err: any) => {
        toast.error(
          err?.response?.data?.message || "Gagal mengambil data rollback!"
        );
      });
  };

  const handleSearchKey = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsLoading(true);
    setSearchKey(e.target.value === "" ? null : e.target.value);
    getBranchProducts(query?.page || 1, e.target.value, query?.limit || null);
    setQuery({
      page: currentPage,
      search: e.target.value === "" ? null : e.target.value,
    });
  };

  const debounceRes = React.useMemo(() => {
    return debounce(handleSearchKey, 500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectVersion = (item: any) => {
    setSelectedVersion(item);
  };

  const confirmRollback = () => {
    setIsLoadingRollback(true);
    rollbackBranchStock(slug, selectedVersion?.id)
      .then((res) => {
        onClose();
        setIsLoadingRollback(false);
        if (res.status === 200) {
          toast.success(res.data?.message || "Berhasil rollback stok!");
          getBranchProducts(currentPage, searchKey, limit);
        }
      })
      .catch((err) => {
        setIsLoadingRollback(false);
        toast.error(
          err?.response?.data?.message || "Gagal melakukan rollback stok!"
        );
      });
  };

  const updateGoodsStock = () => {
    const storeSlug = good?.store?.slug;
    const productSlug = good?.products?.slug;
    const stock = getValues("stock")
      ? parseInt(getValues("stock"))
      : good?.stock;

    try {
      updateBranchGoodStock(storeSlug, productSlug, stock)
        .then((res) => {
          if (res.status === 200) {
            toast.success("Berhasil memperbarui stok barang!");
            getBranchProducts(currentPage, searchKey, limit);
            onClose();
          } else {
            toast.error("Gagal memperbarui stok barang!");
          }
        })
        .catch((err) => {
          toast.error(
            err?.response?.data?.message || "Gagal memperbarui stok barang!"
          );
        });
    } catch (error) {
      toast.error("Gagal memperbarui stok barang!");
    }
  };

  React.useEffect(() => {
    getBranchProducts(
      query?.page || 1,
      query?.search || null,
      query?.limit || null
    );

    return () => {
      debounceRes.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div className="mt-5">
        <div className="flex w-full items-center justify-between gap-10">
          <div className="col-span-8 flex h-12 w-full flex-3 items-center rounded-full border-2 border-gray-300 bg-gray-100 text-navy-700 dark:bg-navy-900 dark:text-white">
            <p className="pl-3 pr-2 text-xl">
              <FiSearch className="h-4 w-4 text-gray-400 dark:text-white" />
            </p>
            <input
              type="text"
              placeholder="Coba cari nama barang..."
              onChange={(e) => debounceRes(e)}
              defaultValue={searchKey || query.search}
              className="block h-full w-full rounded-full bg-gray-100 text-sm font-medium text-navy-700 outline-none placeholder:!text-gray-400 dark:bg-navy-900 dark:text-white dark:placeholder:!text-white"
            />
          </div>
          <div className="flex-1 flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Select
                options={totalItemsOptions}
                placeholder="Total Item"
                classNames={{
                  placeholder: () => `!text-brand-500 dark:text-white/50`,
                  control: () =>
                    `flex !h-12 w-full items-center justify-center !rounded-3xl font-semibold !bg-white !pb-3 !pl-3 !pt-1 text-sm !outline-none transition-all duration-300 !border-none dark:!border-white/10 dark:text-white !cursor-pointer !bg-lightPrimary`,
                  container: () => `w-32`,
                  dropdownIndicator: () => `!text-brand-500`,
                  indicatorSeparator: () => `!hidden`,
                  option: () => `!cursor-pointer`,
                }}
                getOptionLabel={(option) => `${option.value} item`}
                onChange={(val) => {
                  setLimit(val?.value || 10);
                  getBranchProducts(currentPage, searchKey, val?.value || 10);
                  setQuery({
                    page: currentPage,
                    search: searchKey,
                    limit: val?.value || 10,
                  });
                }}
              />
            </div>
          </div>
        </div>

        <div className="mt-10 flex w-full items-center justify-end gap-10">
          <div className="flex w-full items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                size="fit"
                variant="primary"
                children={
                  <div className="flex items-center gap-1">
                    <GoPackageDependencies />{" "}
                    <span className="whitespace-nowrap">Assign Barang</span>
                  </div>
                }
                onClick={() => {
                  navigate(`/admin/branches/${slug}/assign`);
                }}
              />
              <Button
                size="fit"
                variant="outlined"
                children={
                  <div className="flex items-center gap-1">
                    <GoPackageDependencies />{" "}
                    <span className="whitespace-nowrap">CSV Assign Barang</span>
                  </div>
                }
                onClick={() => {
                  navigate(`/admin/branches/${slug}/assign-csv`);
                }}
              />
            </div>
            <Button
              size="fit"
              variant="gray"
              children={
                <div className="flex items-center gap-1">
                  <GoHistory />{" "}
                  <span className="whitespace-nowrap">Rollback Stock</span>
                </div>
              }
              onClick={() => {
                onOpen();
                getRollbackList();
              }}
            />
          </div>
        </div>

        <div className="mt-5">
          {!isLoading ? (
            goods?.length > 0 ? (
              <div className="grid grid-cols-3 gap-3">
                {goods?.map((good, idx) => (
                  <div key={idx} className="col-span-1">
                    <ProductCard
                      id={good?.id.toString()}
                      title={good?.products?.name}
                      category_detail={good?.products?.category_detail}
                      category={good?.products?.category}
                      price={good?.products?.price}
                      sku={good?.products?.sku}
                      description={good?.products?.description}
                      thumbnail={good?.products?.image_thumbnail}
                      images={good?.products?.images}
                      slug={good?.products?.slug}
                      stock={good?.stock}
                      onClickEdit={() => {
                        onOpen();
                        setIsModalStockOpen(true);
                        setValue("stock", good?.stock);
                        setGood(good);
                      }}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex h-80 items-center justify-center">
                <p className="text-lg font-medium text-gray-600 dark:text-gray-400">
                  Belum ada barang yang ditambahkan
                </p>
              </div>
            )
          ) : (
            <div className="flex h-80 w-full flex-col items-center justify-center">
              <Spinner size="xl" className="text-brand-400" />
            </div>
          )}
        </div>
        {!isLoading && goods?.length > 0 && (
          <div className="pagination my-5 flex w-full justify-center">
            <div className="pagination__item flex items-center gap-2">
              <button
                className="pagination__button"
                onClick={() => handlePrevPage()}
              >
                <BiChevronLeft className="text-xl" />
              </button>
              <div className="flex items-center justify-center gap-1">
                {pageState.startIdx >= 1 && (
                  <button
                    className="pagination__button"
                    onClick={() => jumpBackwardPage()}
                  >
                    <PiDotsThreeBold className="text-lg" />
                  </button>
                )}
                {pageNumbers
                  .slice(pageState.startIdx, pageState.endIdx)
                  .map((page, idx) => (
                    <button
                      className={`pagination__button ${
                        currentPage === page ? "pagination__button--active" : ""
                      }`}
                      onClick={() => {
                        setCurrentPage(page);
                        getBranchProducts(page, searchKey, limit);
                        setQuery({
                          page: page,
                          search: searchKey,
                          limit: limit,
                        });
                      }}
                      key={idx}
                    >
                      {page}
                    </button>
                  ))}
                {pageState.endIdx <= lastPage && (
                  <button
                    className="pagination__button"
                    onClick={() => jumpForwardPage()}
                  >
                    <PiDotsThreeBold className="text-lg" />
                  </button>
                )}
              </div>
              <button
                className="pagination__button"
                onClick={() => handleNextPage()}
              >
                <BiChevronRight className="text-xl" />
              </button>
            </div>
          </div>
        )}
      </div>
      {createPortal(
        <CommonModal
          size="2xl"
          closeOnOverlayClick={false}
          isOpen={isOpen && !isModalStockOpen}
          onClose={() => onClose()}
          children={
            <div>
              <ModalHeader>Rollback Stock Barang</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <section>
                  {isLoadingRollback ? (
                    <div className="mt-5 flex h-28 items-center justify-center">
                      <Spinner size="lg" className="text-brand-400" />
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-3">
                      <p className="col-span-2 text-gray-500">
                        Pilih salah satu versi rollback
                      </p>
                      {rollbackList.length > 0 &&
                        rollbackList
                          .slice(0)
                          .reverse()
                          .map((item) => (
                            <div
                              key={item.id}
                              className={`card__product--small ${
                                selectedVersion?.id === item.id ? "active" : ""
                              }`}
                              onClick={() => handleSelectVersion(item)}
                            >
                              <div className="flex items-center gap-3">
                                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-brand-50 bg-opacity-25">
                                  <GoHistory className="h-6 w-6 text-brand-500" />
                                </div>
                                <div className="flex flex-col">
                                  <p className="text-base font-semibold text-gray-800">
                                    {new Date(
                                      item.updated_at
                                    ).toLocaleDateString("id-ID", {
                                      year: "numeric",
                                      month: "short",
                                      day: "numeric",
                                      hour: "numeric",
                                      minute: "numeric",
                                      timeZone: "Asia/Jakarta",
                                      timeZoneName: "short",
                                    })}
                                  </p>
                                  <p className="line-clamp-2 max-w-[180px] text-sm font-medium text-gray-700">
                                    {item.total_products} barang
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))}
                    </div>
                  )}
                </section>
              </ModalBody>
              <ModalFooter>
                {!isLoadingRollback ? (
                  <div className="flex gap-3">
                    <Button
                      size="fit"
                      children="Batal"
                      variant="gray"
                      onClick={onClose}
                    />
                    <Button
                      size="fit"
                      children={<span className="text-white">Rollback</span>}
                      variant="primary"
                      onClick={() => {
                        confirmRollback();
                      }}
                    />
                  </div>
                ) : (
                  <></>
                )}
              </ModalFooter>
            </div>
          }
        />,
        document.body
      )}
      {createPortal(
        <CommonModal
          size="2xl"
          closeOnOverlayClick={false}
          isOpen={isOpen && isModalStockOpen}
          onClose={() => onClose()}
          children={
            <div>
              <ModalHeader>Perbarui Stock</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <InputField
                  id="stock"
                  label="Stok"
                  placeholder="Masukkan Stok"
                  extra="mb-3"
                  errors={errors}
                  register={register}
                  type="number"
                  variant="outline"
                  validationSchema={{
                    required: "Stok harus diisi!",
                    validate: (value) => {
                      if (value <= 0) return "Stok harus lebih dari 0!";
                    },
                  }}
                />
              </ModalBody>
              <ModalFooter>
                <div className="flex gap-3">
                  <Button
                    size="fit"
                    children="Batal"
                    variant="gray"
                    onClick={onClose}
                  />
                  <Button
                    size="fit"
                    children={<span className="text-white">Simpan</span>}
                    variant="primary"
                    onClick={() => updateGoodsStock()}
                  />
                </div>
              </ModalFooter>
            </div>
          }
        />,
        document.body
      )}
    </>
  );
};

export default BranchProduct;
